import { JSX, ReactNode } from 'react';
import { Fragment } from 'react';
import { Card, CardBody, Skeleton as HeroUISkeleton } from '@heroui/react';
import { twMerge } from 'tailwind-merge';

interface SkeletonBaseProps {
  className?: string;
}

interface SkeletonProps {
  children?: ReactNode;
  /**
   * Only applicable when isLoaded is true.
   */
  className?: string;
  isLoaded?: boolean;
  /**
   * The number of items to display in the skeleton.
   * This is only applicable for the 'cards', 'list' and 'list-with-image' types.
   */
  items?: number;
  /**
   * The type of skeleton to display.
   * - 'article' for an article layout
   * - 'avatar' for an avatar layout
   * - 'card' for a card layout
   * - 'cards' for a multi card layout
   * - 'mini-card' for a mini card layout
   * - 'mini-cards' for a multi mini card layout
   * - 'form' for a form layout
   * - 'list' for a list of items
   * - 'list-with-image' for a list of items with images
   * @default 'list'
   */
  type?:
    | 'article'
    | 'avatar'
    | 'card'
    | 'cards'
    | 'mini-card'
    | 'mini-cards'
    | 'form'
    | 'list'
    | 'list-with-image';
}

function ArticleSkeleton({ className }: SkeletonBaseProps): JSX.Element {
  return (
    <div className={twMerge('w-full', className)} data-testid="article-skeleton">
      <HeroUISkeleton className="h-8 w-4/6 rounded-lg mb-2" />
      <HeroUISkeleton className="h-6 w-5/6 rounded-lg mb-6" />
      <div className="space-y-2">
        <HeroUISkeleton className="h-4 rounded-lg" />
        <HeroUISkeleton className="h-4 w-11/12 rounded-lg" />
        <HeroUISkeleton className="h-4 w-5/6 rounded-lg" />
        <HeroUISkeleton className="h-4 rounded-lg" />
        <HeroUISkeleton className="h-4 w-11/12 rounded-lg" />
      </div>
    </div>
  );
}

function AvatarSkeleton({ className }: SkeletonBaseProps): JSX.Element {
  return (
    <div
      className={twMerge('w-full flex items-center gap-2 max-w-xs', className)}
      data-testid="avatar-skeleton"
    >
      <HeroUISkeleton className="size-12 rounded-full" />
      <div className="flex-1">
        <HeroUISkeleton className="h-4 w-4/5 rounded-lg mb-2" />
        <HeroUISkeleton className="h-4 w-3/5 rounded-lg" />
      </div>
    </div>
  );
}

function CardSkeleton({ className }: SkeletonBaseProps): JSX.Element {
  return (
    <div className={twMerge('w-full', 'max-w-xs', className)} data-testid="card-skeleton">
      <div className="flex flex-col gap-8 border-1 border-default p-4 rounded-xl ">
        <div className="flex items-start gap-4">
          <HeroUISkeleton className="size-8 rounded-full shrink-0" />
          <div className="flex-1 space-y-2">
            <HeroUISkeleton className="h-4 w-5/6 rounded-lg" />
            <HeroUISkeleton className="h-3 w-4/6 rounded-lg" />
          </div>
        </div>
        <div className="space-y-2">
          <HeroUISkeleton className="h-4 rounded-lg" />
          <HeroUISkeleton className="h-4 w-11/12 rounded-lg" />
          <HeroUISkeleton className="h-4 w-5/6 rounded-lg" />
          <HeroUISkeleton className="h-4 rounded-lg" />
          <HeroUISkeleton className="h-4 w-11/12 rounded-lg" />
        </div>
        <div className="space-y-2">
          <HeroUISkeleton className="h-3 rounded-lg" />
          <HeroUISkeleton className="h-3 w-11/12 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function FormSkeleton({ className }: SkeletonBaseProps): JSX.Element {
  return (
    <div className={twMerge('w-full flex flex-col', className)} data-testid="form-skeleton">
      <div className="space-y-5">
        <div className="flex flex-col gap-2">
          <HeroUISkeleton className="h-4 w-2/6 rounded-lg" />
          <HeroUISkeleton className="h-8 w-full rounded-lg" />
        </div>

        <div className="flex flex-col gap-2">
          <HeroUISkeleton className="h-4 w-3/6 rounded-lg" />
          <HeroUISkeleton className="h-24 w-full rounded-lg" />
        </div>

        <div className="flex flex-col gap-2">
          <HeroUISkeleton className="h-4 w-3/6 rounded-lg" />
          <HeroUISkeleton className="h-8 w-full rounded-lg" />
        </div>

        <div className="flex flex-col gap-2">
          <HeroUISkeleton className="h-4 w-2/6 rounded-lg" />
          <HeroUISkeleton className="h-8 w-full rounded-lg" />
        </div>

        <div className="flex flex-col gap-2">
          <HeroUISkeleton className="h-4 w-3/6 rounded-lg" />
          <HeroUISkeleton className="h-8 w-full rounded-lg" />
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-8">
        <HeroUISkeleton className="h-9 w-24 rounded-lg" /> {/* Cancel button */}
        <HeroUISkeleton className="h-9 w-24 rounded-lg" /> {/* Submit button */}
      </div>
    </div>
  );
}

function ImageRowSkeleton({ className }: SkeletonBaseProps): JSX.Element {
  return (
    <div
      className={twMerge('w-full h-20 flex items-start gap-4', className)}
      data-testid="image-row-skeleton"
    >
      <div>
        <HeroUISkeleton className="flex rounded-full size-14" />
      </div>
      <div className="flex flex-col w-2/6 gap-3">
        <HeroUISkeleton className="h-4 w-3/5 rounded-lg" />
        <HeroUISkeleton className="h-6 w-full rounded-lg" />
      </div>
      <HeroUISkeleton className="h-4 w-1/6 rounded-lg" />
      <HeroUISkeleton className="h-4 w-1/6 rounded-lg" />
      <HeroUISkeleton className="h-4 w-1/6 rounded-lg" />
    </div>
  );
}

function MiniCardSkeleton({ className }: SkeletonBaseProps): JSX.Element {
  return (
    <Card
      className={twMerge('w-full h-32 max-w-xs border-1 border-default', className)}
      data-testid="mini-card-skeleton"
    >
      <CardBody className="gap-4">
        <HeroUISkeleton className="h-4 w-3/6 rounded-lg" />
        <HeroUISkeleton className="h-4 w-3/12 rounded-lg" />
        <HeroUISkeleton className="mt-auto h-4 w-2/6 rounded-lg" />
      </CardBody>
    </Card>
  );
}

function TextRowSkeleton({ className }: SkeletonBaseProps): JSX.Element {
  return (
    <div
      className={twMerge('w-full h-6 flex items-center gap-4', className)}
      data-testid="text-row-skeleton"
    >
      <HeroUISkeleton className="h-4 w-4/12 rounded-lg" />
      <HeroUISkeleton className="h-4 w-3/12 rounded-lg" />
      <HeroUISkeleton className="h-4 w-1/12 rounded-lg" />
      <HeroUISkeleton className="h-4 w-2/12 rounded-lg" />
      <HeroUISkeleton className="h-4 w-2/12 rounded-lg" />
    </div>
  );
}

export function Skeleton(props: SkeletonProps): JSX.Element | ReactNode {
  const { children, className, isLoaded = false, items, type = 'list' } = props;

  const withImage = type === 'list-with-image';
  let content: ReactNode;

  switch (type) {
    case 'article': {
      content = <ArticleSkeleton className={className} />;

      break;
    }
    case 'avatar': {
      content = <AvatarSkeleton className={className} />;

      break;
    }
    case 'card': {
      content = <CardSkeleton className={className} />;

      break;
    }
    case 'cards': {
      content = (
        <div
          className={twMerge('w-full flex flex-wrap justify-center gap-8', className)}
          data-testid="cards-skeleton"
        >
          {Array.from({ length: items ?? 3 }, (_, index) => (
            <Fragment key={index}>
              <CardSkeleton />
            </Fragment>
          ))}
        </div>
      );

      break;
    }
    case 'form': {
      content = <FormSkeleton className={className} />;

      break;
    }
    case 'list':
    case 'list-with-image': {
      content = (
        <div className="w-full flex flex-col gap-4" data-testid="list-skeleton">
          {Array.from({ length: items ?? (withImage ? 3 : 5) }, (_, index) => (
            <Fragment key={index}>
              {withImage ? <ImageRowSkeleton /> : <TextRowSkeleton />}
            </Fragment>
          ))}
        </div>
      );

      break;
    }
    case 'mini-cards': {
      content = (
        <div className="w-full flex gap-4" data-testid="mini-cards-skeleton">
          {Array.from({ length: items ?? (withImage ? 3 : 5) }, (_, index) => (
            <Fragment key={index}>
              <MiniCardSkeleton />
            </Fragment>
          ))}
        </div>
      );

      break;
    }
    case 'mini-card': {
      content = <MiniCardSkeleton className={className} />;

      break;
    }
    default: {
      content = <HeroUISkeleton className={className} />;
    }
  }

  if (isLoaded) {
    return children;
  }

  return content;
}
